import { useAnalyticsTracking } from '@/analytics/snowplow/AnalyticsTrackingContext';
import { useEmitUiEvent } from '@/analytics/snowplow/useEmitUIEvent';
import { schema } from '@/components/Patient/forms/PatientInformation/PatientInformation.schema';
import { ATSI_LIST_OPTIONS } from '@/components/Patient/Patient.constants';
import { getAuthData } from '@/data/service/authService';
import { useGetDoctors } from '@/hooks/rest/useGetDoctors';
import { useAppStore } from '@/state-management';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, TextField, toast } from '@montugroup/design-system';
import { Autocomplete, Checkbox, Grid, Stack, styled, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { grey } from '@mui/material/colors';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { FormContainer, SelectElement, TextFieldElement } from 'react-hook-form-mui';
import type { z } from 'zod';
import { shallow } from 'zustand/shallow';
import FormSection from '../../FormSection';
import { usePostResendPatientConsent } from '../../hooks/usePostResentPatientConsent';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';
import { PatientContext } from '../../PatientContext';

export type PatientInformationFormFields = z.infer<typeof schema>;
export type ResendConsentData = { showConsentFormResendLink: boolean };

export interface PatientInformationProps {
  defaultValues?: PatientInformationFormFields;
  editable?: boolean;
  showConsentFormResendLink: boolean;
  onSubmit?: (data: PatientInformationFormFields) => void;
  onResendConsent: (contents: ResendConsentData) => void;
  isLoading?: boolean;
}

const StyledResendConsentButton = styled(Button)({
  textTransform: 'capitalize',
  fontSize: '1rem',
});

const StyledDatePicker = styled(DatePicker)({
  width: '100%',
});

const StyledTextInput = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled: boolean }>(({ isDisabled }) => ({
  ...(isDisabled && {
    opacity: 0.7,
  }),
}));

const isIhiActive = (ihiNumberStatus?: string | null): boolean =>
  !!ihiNumberStatus && ihiNumberStatus.toLowerCase() === 'active';

export const PatientInformation = (props: PatientInformationProps) => {
  const { defaultValues, editable, showConsentFormResendLink, onSubmit, onResendConsent, isLoading } = props;
  const { patientId } = useContext(PatientContext);
  const { isAdmin, isNurse, isManager } = getAuthData();
  const { data: listOfDoctors } = useGetDoctors();
  const { resendConsentForm } = usePostResendPatientConsent(patientId);
  const emitEvent = useEmitUiEvent();
  const { uiEvent } = useAnalyticsTracking();

  const ihiNumberStatus = useAppStore((state) => state.getPatientIdentificationInfo().ihiNumberStatus);

  const isCalendarOpen = useRef(false);
  const dobInputDone = useRef(true);

  const setPatientBasicInfo = useAppStore.use.setPatientBasicInfo();
  const setPatientIhiVerification = useAppStore.use.setPatientIhiVerification();

  const [ihiActive, setIhiActive] = useState<boolean>(isIhiActive(ihiNumberStatus));

  const listOfDoctorsForSelect =
    listOfDoctors?.filter(Boolean)?.map((doctor) => {
      return { id: doctor.doctor_id, label: `${doctor.doctor_name} - ${doctor.id}` };
    }) ?? [];

  const listOfAtsiOptionsForSelect = ATSI_LIST_OPTIONS?.map((label) => {
    return { id: label, label };
  });

  const handleResendConsentForm = useCallback(async () => {
    resendConsentForm(undefined, {
      onSuccess: () => {
        toast.success('Consent Form Link is sent via Email');
        onResendConsent({ showConsentFormResendLink: false });
      },
      onError: () => {
        toast.error('Failed to resend Consent Form. Please try again.');
      },
    });

    emitEvent({
      component_name: 'ConsentRequestButton',
      event_name: 'send_request_consent',
      id: 'send_request_btn',
      context_id: uiEvent?.context_id,
    });
  }, [resendConsentForm, onResendConsent, emitEvent, uiEvent?.context_id]);

  const formContext = useForm<PatientInformationFormFields>({
    defaultValues,
    values: defaultValues,
    resolver: zodResolver(schema),
  });

  const {
    control,
    setValue,
    formState: { errors },
  } = formContext;

  /*
    Listen to the 'basicInfo' values in the store. If they change (via IHI update
    or via other external triggers) then we should update the relevant form values accordingly.
   */
  useEffect(
    () =>
      useAppStore.subscribe(
        (state) => state.patient.basicInfo,
        (basicInfo) => {
          for (const field in basicInfo) {
            setValue(field as keyof PatientInformationFormFields, basicInfo[field as keyof typeof basicInfo]);
          }
        },
        {
          equalityFn: shallow,
        },
      ),
    [setValue],
  );

  useEffect(
    () =>
      useAppStore.subscribe(
        (state) => state.patient.identificationInfo.ihiNumberStatus,
        (ihiNumberStatus) => {
          setIhiActive(isIhiActive(ihiNumberStatus));
        },
        {
          equalityFn: shallow,
        },
      ),
    [],
  );

  const isEffectivelyEditable = editable && typeof onSubmit === 'function';

  const isIhiFieldEditable = isEffectivelyEditable;
  const formName = PATIENT_DETAILS_FORM_NAMES.PATIENT_BASIC_INFO;

  const handleSubmit = (attributes: PatientInformationFormFields) => {
    if (onSubmit) {
      onSubmit(attributes);

      setPatientBasicInfo({
        ...(attributes.firstName ? { firstName: attributes.firstName } : null),
        ...(attributes.lastName ? { lastName: attributes.lastName } : null),
        ...(attributes.dob ? { dob: attributes.dob } : null),
        ...(attributes.gender ? { gender: attributes.gender } : null),
      });
    }
  };

  const handleOnSuccess = (attributes: Partial<PatientInformationFormFields>) => {
    const fieldsThatTriggerIhiVerification = ['firstName', 'lastName', 'gender', 'dob'];

    const shouldTriggerIhiVerification = Object.keys(attributes).some((key) => {
      if (key === 'dob') {
        // We only allow IHI verification if the user has completed entering the date of birth
        return dobInputDone.current;
      }
      return fieldsThatTriggerIhiVerification.includes(key);
    });

    if (shouldTriggerIhiVerification) {
      setPatientIhiVerification(true);
    }
  };

  return (
    <FormSection
      title="Patient Information"
      formName={formName}
      formContext={formContext}
      onSubmit={handleSubmit}
      onSuccess={handleOnSuccess}
      isLoading={isLoading}
    >
      <FormContainer formContext={formContext as unknown as UseFormReturn<FieldValues>}>
        <Grid container columnSpacing={10} rowSpacing={5} marginY={2} data-testid="patient-information-form">
          <Grid item xs={12} sm={12} lg={6}>
            <Tooltip
              title={ihiActive ? `Patient’s first name cannot be edited as their IHI has already been verified.` : ''}
              disableHoverListener={!ihiActive}
            >
              <div>
                <StyledTextInput isDisabled={ihiActive}>
                  <TextFieldElement
                    name="firstName"
                    label="First Name"
                    fullWidth
                    disabled={!isIhiFieldEditable}
                    InputProps={{
                      readOnly: ihiActive,
                    }}
                  />
                </StyledTextInput>
              </div>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <Tooltip
              title={ihiActive ? `Patient’s last name cannot be edited as their IHI has already been verified.` : ''}
              disableHoverListener={!ihiActive}
            >
              <div>
                <StyledTextInput isDisabled={ihiActive}>
                  <TextFieldElement
                    name="lastName"
                    label="Last Name"
                    fullWidth
                    disabled={!isIhiFieldEditable}
                    InputProps={{
                      readOnly: ihiActive,
                    }}
                  />
                </StyledTextInput>
              </div>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <Controller
              name="dob"
              control={formContext.control}
              render={({ field: { onChange, value } }) => {
                return (
                  <StyledDatePicker
                    label="DoB"
                    value={value ? DateTime.fromISO(value).toUTC().startOf('day') : null}
                    onChange={(newValue) => {
                      if (newValue instanceof DateTime) {
                        const normalized = newValue.startOf('day');
                        onChange(normalized.toFormat("yyyy-MM-dd'T'00:00:00'Z'"));
                      }
                    }}
                    onOpen={() => {
                      isCalendarOpen.current = true;
                      dobInputDone.current = false;
                    }}
                    onAccept={() => {
                      // User has finished interacting with the calendar,
                      // so let's set it to true, so we can allow IHI verification
                      dobInputDone.current = true;
                    }}
                    onClose={() => {
                      isCalendarOpen.current = false;
                      dobInputDone.current = true;
                    }}
                    maxDate={DateTime.now().minus({ years: 18 })}
                    format="dd/MM/yyyy"
                    disabled={!isIhiFieldEditable}
                    slotProps={{
                      textField: {
                        error: !!errors?.dob,
                        helperText: errors?.dob?.message?.toString() ?? '',
                        onBlur: () => {
                          if (isCalendarOpen.current) {
                            // If the calendar is open, don't verify IHI yet
                            dobInputDone.current = false;
                          }
                        },
                      },
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <TextFieldElement name="gender" label="Gender" fullWidth disabled={!isIhiFieldEditable} />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <SelectElement
              name="atsiStatus"
              label="Aboriginal / Torres Strait Islander"
              options={listOfAtsiOptionsForSelect}
              fullWidth
              data-testid={'atsi-status'}
              disabled={!isEffectivelyEditable}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            {(isAdmin || isNurse || isManager) && (
              <Controller
                name="doctorId"
                control={control}
                render={({ field: { onChange, value } }) => {
                  const selectedDoctor = listOfDoctorsForSelect.find((doctor) => doctor.id === value);
                  return (
                    <Autocomplete
                      options={listOfDoctorsForSelect}
                      getOptionLabel={(option) => option.label}
                      value={selectedDoctor || null}
                      onChange={(_, newValue) => onChange(newValue ? newValue.id : null)}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Doctor"
                          error={!!errors.doctorId}
                          helperText={errors.doctorId?.message}
                        />
                      )}
                      disabled={!isEffectivelyEditable}
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            <Stack flexDirection={'row'} alignItems={'center'}>
              <Typography color={grey[500]}>Consent</Typography>
              <Checkbox checked={Boolean(defaultValues?.consent)} name="consent" disabled={true} />
              {showConsentFormResendLink && (
                <StyledResendConsentButton onClick={handleResendConsentForm}>
                  Resend Consent Form
                </StyledResendConsentButton>
              )}
            </Stack>
          </Grid>
        </Grid>
      </FormContainer>
    </FormSection>
  );
};
